import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { SystemLocale } from '@ninja/utilities';
import { AppPlatformContextProvider, GlobalAPIError } from '@shory-health-individual/customer-lib';
import { useWebNavigation } from '../contexts/navigation';
import { useToastApiError } from '../hooks/useToastApiError';
import { AppConfig } from './app-config';

type Props = {
	children: React.ReactNode;
	locale: SystemLocale;
	liveChat: {
		startChat: () => void;
	};
};

const AppProvider = (props: Props) => {
	const { children, locale, liveChat } = props;
	const { t } = useTranslation();
	const { data, update } = useSession();
	const { navigationPush } = useWebNavigation();
	const { showToastApiError } = useToastApiError();

	return (
		<AppPlatformContextProvider
			locale={locale}
			translate={t}
			platform="web"
			onGeneralErrorHandling={async (error: GlobalAPIError) => {
				const errorCode = error.mapped?.[0].code;

				if (errorCode === 'PaymentPending') {
					await navigationPush('QuotePaymentPending');
				} else if (errorCode === 'EffectiveDateFromIsInPastDate') {
					await navigationPush('EffectiveDateExpired');
				} else if (errorCode === 'QuoteExpired') {
					await navigationPush('QuoteExpired');
				} else if (errorCode === 'QuoteAlreadyPurchased') {
					await navigationPush('QuoteAlreadyPurchased');
				} else if (errorCode === 'userIsBlackListed') {
					await navigationPush('BlackListedUser');
				} else {
					showToastApiError(error);
				}
			}}
			userEmail={data?.user?.email}
			liveChatWidgetProvider={liveChat}
			apiConnectionOptions={{
				locale: locale,
				appType: 'customer',
				apiURL: AppConfig.healthIndividualApiUrl,
				apiToken: data?.tpToken,
				refreshToken: async () => {
					const res = await update();

					return res?.tpToken;
				},
			}}
		>
			<>{children}</>
		</AppPlatformContextProvider>
	);
};

export default AppProvider;
